import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Field,
  Input,
  Textarea,
  Switch,
  Dropdown,
  Option,
  Combobox,
  makeStyles,
  tokens,
  Text,
  Divider,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { CheckmarkCircle24Regular, Add24Regular } from '@fluentui/react-icons';
import { useAuth } from '@/components/auth/AuthProvider';
import { useCreateReferral } from '@/api/hooks/useReferrals';
import { useMyProfile } from '@/api/hooks/useGraphUsers';
import { isAuthEnabled } from '@/config/env';
import { ATA_SERVICES, ATA_LOCATIONS } from '@/config/services';

// ─── Validation Schema ────────────────────────────────────────────────────────

const ReferralFormSchema = z
  .object({
    // Referrer
    referrerName: z.string().min(1, 'Name is required'),
    referrerEmail: z.string().email('Enter a valid email address'),
    referrerTitle: z.string().optional(),
    referrerLocation: z.string().min(1, 'Location is required'),

    // Client
    businessName: z.string().optional(),
    clientContactName: z.string().min(1, 'Contact name is required'),
    clientEmail: z.string().email('Enter a valid client email'),
    clientPhone: z.string().min(7, 'Enter a valid phone number'),
    isExistingClient: z.boolean(),
    clientNumber: z.string().optional(),

    // Services & notes
    services: z.array(z.string()).min(1, 'Select at least one service'),
    referrerComments: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.isExistingClient && !data.clientNumber?.trim()) {
        return false;
      }
      return true;
    },
    {
      message: 'Client number is required for existing clients',
      path: ['clientNumber'],
    },
  );

type ReferralFormValues = z.infer<typeof ReferralFormSchema>;

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    maxWidth: '720px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    flexWrap: 'wrap',
    paddingTop: tokens.spacingVerticalS,
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  servicesCombobox: {
    width: '100%',
  },
  selectedServices: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  serviceTag: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
});

// ─── Component ────────────────────────────────────────────────────────────────

export function ReferralForm() {
  const styles = useStyles();
  const { userName, userEmail } = useAuth();
  const createReferral = useCreateReferral();
  const { data: myProfile } = useMyProfile(isAuthEnabled);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ReferralFormValues>({
    resolver: zodResolver(ReferralFormSchema),
    defaultValues: {
      referrerName: userName ?? '',
      referrerEmail: userEmail ?? '',
      referrerTitle: '',
      referrerLocation: '',
      businessName: '',
      clientContactName: '',
      clientEmail: '',
      clientPhone: '',
      isExistingClient: false,
      clientNumber: '',
      services: [],
      referrerComments: '',
    },
  });

  // Sync auth values into form when they become available
  useEffect(() => {
    if (userName) setValue('referrerName', userName);
    if (userEmail) setValue('referrerEmail', userEmail);
  }, [userName, userEmail, setValue]);

  // Sync Graph profile (title & location) into form
  useEffect(() => {
    if (!myProfile) return;
    reset((prev) => {
      const updates: Partial<ReferralFormValues> = {};
      if (myProfile.jobTitle && !prev.referrerTitle) {
        updates.referrerTitle = myProfile.jobTitle;
      }
      if (myProfile.officeLocation && !prev.referrerLocation) {
        const match = ATA_LOCATIONS.find(
          (loc) => loc.toLowerCase() === myProfile.officeLocation!.toLowerCase(),
        );
        if (match) updates.referrerLocation = match;
      }
      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [myProfile, reset]);

  const isExistingClient = watch('isExistingClient');
  const selectedServices = watch('services');

  const onSubmit = async (values: ReferralFormValues) => {
    await createReferral.mutateAsync({
      referrerName: userName ?? values.referrerName,
      referrerEmail: userEmail ?? values.referrerEmail,
      referrerTitle: values.referrerTitle ?? undefined,
      referrerLocation: values.referrerLocation,
      businessName: values.businessName ?? undefined,
      clientContactName: values.clientContactName,
      clientEmail: values.clientEmail,
      clientPhone: values.clientPhone,
      isExistingClient: values.isExistingClient,
      clientNumber: values.isExistingClient ? (values.clientNumber ?? undefined) : undefined,
      services: values.services,
      referrerComments: values.referrerComments ?? undefined,
    });
    setSubmitted(true);
  };

  const handleSubmitAnother = () => {
    reset({
      referrerName: userName ?? '',
      referrerEmail: userEmail ?? '',
      referrerTitle: '',
      referrerLocation: '',
      businessName: '',
      clientContactName: '',
      clientEmail: '',
      clientPhone: '',
      isExistingClient: false,
      clientNumber: '',
      services: [],
      referrerComments: '',
    });
    setSubmitted(false);
  };

  // ─── Success state ──────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className={styles.successBox}>
        <CheckmarkCircle24Regular
          style={{ fontSize: '64px', color: tokens.colorPaletteGreenForeground1 }}
        />
        <div>
          <Text size={500} weight="semibold" block>
            Referral Submitted!
          </Text>
          <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
            Thank you. Our team will review and follow up on this referral.
          </Text>
        </div>
        <Button appearance="primary" icon={<Add24Regular />} onClick={handleSubmitAnother}>
          Submit Another Referral
        </Button>
      </div>
    );
  }

  // ─── Error banner ───────────────────────────────────────────────────────────

  const mutationError = createReferral.error;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.form}>
        {mutationError && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Submission Failed</MessageBarTitle>
              {mutationError instanceof Error
                ? mutationError.message
                : 'An unexpected error occurred. Please try again.'}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* ── Section 1: Team Member Information ── */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle} block>
            Team Member Information
          </Text>
          <Divider />

          <div className={styles.grid2}>
            <Field label="Your Name" required validationMessage={errors.referrerName?.message}>
              <Controller
                name="referrerName"
                control={control}
                render={({ field }) => (
                  <Input {...field} placeholder="Jane Smith" />
                )}
              />
            </Field>

            <Field label="Your Email" required validationMessage={errors.referrerEmail?.message}>
              <Controller
                name="referrerEmail"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="email"
                    placeholder="jane@ata.net"
                    inputMode="email"

                  />
                )}
              />
            </Field>

            <Field label="Title / Role" validationMessage={errors.referrerTitle?.message}>
              <Controller
                name="referrerTitle"
                control={control}
                render={({ field }) => (
                  <Input {...field} value={field.value ?? ''} placeholder="Senior Tax Manager" />
                )}
              />
            </Field>

            <Field label="Location" required validationMessage={errors.referrerLocation?.message}>
              <Controller
                name="referrerLocation"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    placeholder="Select your office"
                    value={field.value}
                    selectedOptions={field.value ? [field.value] : []}
                    onOptionSelect={(_e, data) => field.onChange(data.optionValue ?? '')}
                  >
                    {ATA_LOCATIONS.map((loc) => (
                      <Option key={loc} value={loc}>
                        {loc}
                      </Option>
                    ))}
                  </Dropdown>
                )}
              />
            </Field>
          </div>
        </div>

        {/* ── Section 2: Client Information ── */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle} block>
            Client Information
          </Text>
          <Divider />

          <div className={styles.grid2}>
            <Field
              label="Business Name"
              validationMessage={errors.businessName?.message}
              style={{ gridColumn: '1 / -1' }}
            >
              <Input {...register('businessName')} placeholder="Acme Corp (optional)" />
            </Field>

            <Field
              label="Contact Name"
              required
              validationMessage={errors.clientContactName?.message}
            >
              <Input {...register('clientContactName')} placeholder="John Doe" />
            </Field>

            <Field label="Contact Email" required validationMessage={errors.clientEmail?.message}>
              <Input
                {...register('clientEmail')}
                type="email"
                placeholder="john@acme.com"
                inputMode="email"
              />
            </Field>

            <Field label="Contact Phone" required validationMessage={errors.clientPhone?.message}>
              <Input
                {...register('clientPhone')}
                type="tel"
                placeholder="(555) 555-5555"
                inputMode="tel"
              />
            </Field>

            <Field label="Existing ATA Client?" style={{ alignSelf: 'center' }}>
              <Controller
                name="isExistingClient"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={(_e, data) => field.onChange(data.checked)}
                    label={field.value ? 'Yes' : 'No'}
                  />
                )}
              />
            </Field>

            {isExistingClient && (
              <Field
                label="Client Number"
                required
                validationMessage={errors.clientNumber?.message}
                style={{ gridColumn: '1 / -1' }}
              >
                <Input {...register('clientNumber')} placeholder="ATA client number" />
              </Field>
            )}
          </div>
        </div>

        {/* ── Section 3: Services of Interest ── */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle} block>
            Services of Interest
          </Text>
          <Divider />

          <Field label="Select Services" required validationMessage={errors.services?.message}>
            <Controller
              name="services"
              control={control}
              render={({ field }) => (
                <>
                  <Combobox
                    className={styles.servicesCombobox}
                    placeholder="Click to select services..."
                    multiselect
                    selectedOptions={field.value}
                    onOptionSelect={(_e, data) => {
                      const next = data.selectedOptions ?? [];
                      field.onChange(next);
                    }}
                    value=""
                  >
                    {ATA_SERVICES.map((svc) => (
                      <Option key={svc} value={svc} text={svc}>
                        {svc}
                      </Option>
                    ))}
                  </Combobox>
                  {selectedServices.length > 0 && (
                    <div className={styles.selectedServices} role="list" aria-label="Selected services">
                      {selectedServices.map((svc) => (
                        <span key={svc} className={styles.serviceTag} role="listitem">
                          {svc}
                          <button
                            type="button"
                            onClick={() => field.onChange(field.value.filter((s) => s !== svc))}
                            aria-label={`Remove ${svc}`}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              color: 'inherit',
                              fontSize: '12px',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            />
          </Field>
        </div>

        {/* ── Section 4: Additional Comments ── */}
        <div className={styles.section}>
          <Field label="Additional Comments" validationMessage={errors.referrerComments?.message}>
            <Textarea
              {...register('referrerComments')}
              placeholder="Any additional context about this referral opportunity..."
              rows={4}
              resize="vertical"
            />
          </Field>
        </div>

        {/* ── Submit ── */}
        <div className={styles.actions}>
          <Button
            type="submit"
            appearance="primary"
            disabled={isSubmitting || createReferral.isPending}
            icon={
              (isSubmitting || createReferral.isPending) ? (
                <Spinner size="tiny" />
              ) : undefined
            }
            size="large"
          >
            {isSubmitting || createReferral.isPending ? 'Submitting...' : 'Submit Referral'}
          </Button>
        </div>
      </div>
    </form>
  );
}
